import React, { useEffect, useMemo, useState } from 'react';
import {
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  RadioGroup,
  Radio,
  Alert,
  AlertIcon,
  AlertDescription,
  FormErrorMessage,
  Checkbox,
} from '@chakra-ui/react';

import SelectSearch from 'components/fields/SelectSearch';

import Card from 'components/card/Card';
import DateField from 'components/fields/DateField';
import TimeField from 'components/fields/TimeField';
import { useAppSelector, useAppDispatch } from 'hooks';
import { useCarriers } from 'hooks/use-carriers-hook';
import { useCommercialAutoSigningEnabled } from 'hooks/useCommercialAutoSigningEnabled';
import {
  RUNNER_COMMERCIAL_AUTO,
  RUNNER_COMMERCIAL_PROPERTY_CASUALTY,
} from 'variables/constants';
import { PROVINCES } from 'variables/options';
import {
  setInsuranceCompany,
  setEffectiveDate,
  setEffectiveDateTime,
  setExpiryDate,
  setBinderPolicyNumber,
  setBusinessType,
  setPremium,
  setPremiumConfirmed,
  setIsLoa,
  setClientName,
  setCustomerCode,
  setPaymentMethod,
  setIncludeBilling,
  setApplicantEmail,
  setApplicantAddressField,
} from 'reducers/commercialFormReducer';

// Markets that trigger warning message (includes Apollo which does NOT require agency billing)
const WARNING_MARKETS = [
  'Apollo',
  'Agile UW',
  'Anderson McTague',
  'Angus Miller',
  'April',
  'Burns & Wilcox',
  'BIG Lloyds (AU Gold)',
  'Cansure',
  'Coast Underwriters',
  'Excess Underwriting',
  'Forward Insurance',
  'GroupOne',
  'Premier',
  'Special Risk Insurance Managers (SRIM)',
  'Southwestern Group (SWG)',
  'Totten Group',
];

// Markets that auto-select Agency Billing (Apollo is NOT in this list)
const AGENCY_BILLING_MARKETS = [
  'Agile UW',
  'Anderson McTague',
  'Angus Miller',
  'April',
  'Burns & Wilcox',
  'BIG Lloyds (AU Gold)',
  'Cansure',
  'Coast Underwriters',
  'Excess Underwriting',
  'Forward Insurance',
  'GroupOne',
  'Premier',
  'Special Risk Insurance Managers (SRIM)',
  'Southwestern Group (SWG)',
  'Totten Group',
  'AM Fredericks',
  'CannGen',
  'CFC Underwriting',
  'CHES Special Risk',
  'Chutter',
  'Intact Public Entities',
  'K&K Canada',
  'Markel',
  'MedThree',
  'PAL',
  'Revau',
  'Signature Risk',
  'Strategic Underwriting Managers (SUM)',
  'Trinity Underwriting',
  'Unique Risks',
  'Victor',
];

interface CommercialPolicyInformationProps {
  isLocked?: boolean;
  canEditPremium?: boolean;
}

const CommercialPolicyInformation = ({
  isLocked = false,
  canEditPremium = false,
}: CommercialPolicyInformationProps) => {
  const dispatch = useAppDispatch();
  const isCommercialAutoSigningEnabled = useCommercialAutoSigningEnabled();
  const lineOfBusiness = useAppSelector(
    (state) => state.commercialForm.lineOfBusiness,
  );

  // Determine runner type based on line of business
  const runnerType =
    lineOfBusiness === 'Auto'
      ? RUNNER_COMMERCIAL_AUTO.toString()
      : RUNNER_COMMERCIAL_PROPERTY_CASUALTY.toString();

  const { carriers, carrierOptions, isLoadingCarriers } = useCarriers({
    runner_type: runnerType,
  });

  const insuranceCompany = useAppSelector(
    (state) => state.commercialForm.data.insurance_company,
  );
  const effectiveDate = useAppSelector(
    (state) => state.commercialForm.data.effective_date,
  );
  const effectiveDateTime = useAppSelector(
    (state) => state.commercialForm.data.effective_date_time || '',
  );
  const expiryDate = useAppSelector(
    (state) => state.commercialForm.data.expiry_date,
  );
  const binderPolicyNumber = useAppSelector(
    (state) => state.commercialForm.data.binder_policy_number,
  );
  const businessType = useAppSelector(
    (state) => state.commercialForm.data.business_type,
  );
  const premium = useAppSelector((state) => state.commercialForm.data.premium);
  const isLoa = useAppSelector((state) => {
    const value = state.commercialForm.data.is_loa;
    return typeof value === 'boolean' ? (value ? 'yes' : 'no') : value;
  });
  const clientName = useAppSelector(
    (state) => state.commercialForm.data.client_name,
  );
  const customerCode = useAppSelector(
    (state) => state.commercialForm.data.customer_code,
  );
  const applicantEmail = useAppSelector(
    (state) => state.commercialForm.data.applicant_email || '',
  );
  const applicantAddress = useAppSelector(
    (state) => state.commercialForm.data.applicant_address,
  );
  const isSigningFlow = useAppSelector(
    (state) => state.commercialForm.data.is_signing_flow,
  );
  const billing = useAppSelector((state) => state.commercialForm.data.billing);
  const validationErrors = useAppSelector(
    (state) => state.commercialForm.validationErrors,
  );

  // Local state for premium input to handle partial decimal input
  const [premiumInput, setPremiumInput] = useState<string>('');
  const premiumConfirmed = useAppSelector(
    (state) => state.commercialForm.premiumConfirmed,
  );

  // Sync local state with Redux state
  useEffect(() => {
    setPremiumInput(premium ? premium.toString() : '');
  }, [premium]);

  // Check if selected carrier requires warning
  const showWarning = useMemo(() => {
    if (!insuranceCompany) return false;
    return WARNING_MARKETS.some((market) =>
      insuranceCompany.toLowerCase().includes(market.toLowerCase()),
    );
  }, [insuranceCompany]);

  // Get the selected carrier ID for SelectSearch
  const selectedCarrierId = useMemo(() => {
    if (!insuranceCompany) return null;
    const carrier = carriers.find((c) => c.name === insuranceCompany);
    return carrier?.id || null;
  }, [insuranceCompany, carriers]);

  // Check if selected carrier requires agency billing
  const requiresAgencyBilling = useMemo(() => {
    if (!insuranceCompany) return false;
    return AGENCY_BILLING_MARKETS.some((market) =>
      insuranceCompany.toLowerCase().includes(market.toLowerCase()),
    );
  }, [insuranceCompany]);

  // Auto-select agency billing when required
  useEffect(() => {
    if (requiresAgencyBilling && billing.payment_method !== 'agency_bill') {
      dispatch(setPaymentMethod('agency_bill'));
      dispatch(setIncludeBilling(true));
    }
  }, [requiresAgencyBilling, billing.payment_method, dispatch]);

  const handleInsuranceCompanyChange = (value: string | number | null) => {
    // Find the carrier name from the selected ID
    const selectedCarrier = carriers.find((carrier) => carrier.id === value);
    dispatch(setInsuranceCompany(selectedCarrier?.name || ''));
  };

  const handleClientNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setClientName(event.target.value));
  };

  const handleCustomerCodeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setCustomerCode(event.target.value));
  };

  const parseEffectiveDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    return new Date(
      parseInt(match[1]),
      parseInt(match[2]) - 1,
      parseInt(match[3]),
    );
  };

  const handleEffectiveDateChange = (date: Date | null) => {
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      dispatch(setEffectiveDate(`${year}-${month}-${day}`));
    } else {
      dispatch(setEffectiveDate(''));
    }
  };

  const handleEffectiveDateTimeChange = (time: string) => {
    dispatch(setEffectiveDateTime(time));
  };

  const handleExpiryDateChange = (date: Date | null) => {
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      dispatch(setExpiryDate(`${year}-${month}-${day}`));
    } else {
      dispatch(setExpiryDate(''));
    }
  };

  const handleBinderPolicyNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setBinderPolicyNumber(event.target.value));
  };

  const handleBusinessTypeChange = (value: 'new' | 'remarket') => {
    dispatch(setBusinessType(value));
  };

  const handlePremiumChange = (valueString: string) => {
    setPremiumInput(valueString);
    if (valueString === '') {
      dispatch(setPremium(0));
      return;
    }
    const value = parseFloat(valueString);
    if (!isNaN(value)) {
      dispatch(setPremium(value));
    }
  };

  const handleIsLoaChange = (value: 'yes' | 'no') => {
    dispatch(setIsLoa(value));
  };

  return (
    <Card mb="20px" id="sectionCommercialPolicyInformation">
      <Text fontWeight="bold" fontSize="2xl" mb="20px">
        Policy Information
      </Text>

      <VStack spacing={4} align="stretch">
        {/* Warning for specific markets */}
        {showWarning && (
          <Alert status="warning">
            <AlertIcon />
            <AlertDescription>
              Please submit to Personal Lines all binders for personal marine,
              hard to place primary homeowners policies, personal use vacation
              trailers, tenant package policies, owner occupied condo's.
            </AlertDescription>
          </Alert>
        )}

        <HStack spacing={4} align="flex-end">
          {isCommercialAutoSigningEnabled && (
            <FormControl
              flex={1}
              isRequired
              isInvalid={!!validationErrors?.applicant_email}
            >
              <FormLabel ml="0">Applicant Email</FormLabel>
              <Input
                variant="main"
                type="email"
                value={applicantEmail}
                onChange={(e) => dispatch(setApplicantEmail(e.target.value))}
                placeholder="Enter applicant email"
                isDisabled={isLocked}
                borderColor={!applicantEmail ? 'red' : 'inherit'}
              />
              {validationErrors?.applicant_email && (
                <FormErrorMessage>
                  {validationErrors.applicant_email}
                </FormErrorMessage>
              )}
            </FormControl>
          )}

          <FormControl
            flex={1}
            isRequired
            isInvalid={!!validationErrors?.binder_policy_number}
          >
            <FormLabel ml="0">Binder Number</FormLabel>
            <Input
              variant="main"
              value={binderPolicyNumber}
              onChange={handleBinderPolicyNumberChange}
              placeholder="Enter binder number"
              isDisabled={isLocked}
              borderColor={!binderPolicyNumber ? 'red' : 'inherit'}
            />
            {validationErrors?.binder_policy_number && (
              <FormErrorMessage>
                {validationErrors.binder_policy_number}
              </FormErrorMessage>
            )}
          </FormControl>

          <FormControl
            flex={1}
            isRequired
            isInvalid={!!validationErrors?.effective_date}
          >
            <DateField
              label="Effective Date"
              date={parseEffectiveDate(effectiveDate)}
              onChange={handleEffectiveDateChange}
              required
              isDisabled={isLocked}
            />
            {validationErrors?.effective_date && (
              <FormErrorMessage>
                {validationErrors.effective_date}
              </FormErrorMessage>
            )}
          </FormControl>

          {isCommercialAutoSigningEnabled && (
            <FormControl
              flex={1}
              isRequired
              isInvalid={!!validationErrors?.effective_date_time}
            >
              <TimeField
                label="Effective Time"
                value={effectiveDateTime}
                onChange={handleEffectiveDateTimeChange}
                required
                isDisabled={isLocked}
              />
              {validationErrors?.effective_date_time && (
                <FormErrorMessage>
                  {validationErrors.effective_date_time}
                </FormErrorMessage>
              )}
            </FormControl>
          )}

          {isCommercialAutoSigningEnabled && (
            <FormControl
              flex={1}
              isRequired
              isInvalid={!!validationErrors?.expiry_date}
            >
              <DateField
                label="Expiry Date"
                date={parseEffectiveDate(expiryDate)}
                onChange={handleExpiryDateChange}
                required
                isDisabled={isLocked}
              />
              {validationErrors?.expiry_date && (
                <FormErrorMessage>
                  {validationErrors.expiry_date}
                </FormErrorMessage>
              )}
            </FormControl>
          )}

          {isSigningFlow && (
            <FormControl
              flex={1}
              isRequired
              isInvalid={!!validationErrors?.applicant_address?.province}
            >
              <FormLabel ml="0">Province</FormLabel>
              <SelectSearch
                options={PROVINCES}
                value={applicantAddress.province}
                onChange={(value) =>
                  dispatch(
                    setApplicantAddressField({
                      field: 'province',
                      value: (value as string) || '',
                    }),
                  )
                }
                placeholder="Select province"
                isDisabled={isLocked}
                allowClear
              />
              {validationErrors?.applicant_address?.province && (
                <FormErrorMessage>
                  {validationErrors.applicant_address.province}
                </FormErrorMessage>
              )}
            </FormControl>
          )}

          <FormControl
            flex={1}
            isRequired
            isInvalid={!!validationErrors?.insurance_company}
          >
            <FormLabel ml="0">Insurance Company/MGA</FormLabel>
            <SelectSearch
              options={carrierOptions}
              value={selectedCarrierId}
              onChange={handleInsuranceCompanyChange}
              placeholder="Select Insurance Company"
              isDisabled={
                isLocked || isLoadingCarriers || carriers.length === 0
              }
              allowClear={true}
            />
            {validationErrors?.insurance_company && (
              <FormErrorMessage>
                {validationErrors.insurance_company}
              </FormErrorMessage>
            )}
            {!isLoadingCarriers && carriers.length === 0 && (
              <Text fontSize="sm" color="red.500" mt="5px">
                No carriers are available. Please contact your administrator.
              </Text>
            )}
          </FormControl>
        </HStack>

        <HStack spacing={4} align="flex-end">
          <FormControl
            flex={1}
            isRequired
            isInvalid={!!validationErrors?.business_type}
          >
            <FormLabel ml="0">Business Type</FormLabel>
            <RadioGroup
              value={businessType}
              onChange={handleBusinessTypeChange}
              isDisabled={isLocked}
            >
              <HStack spacing={8}>
                <Radio value="new" isDisabled={isLocked}>
                  New
                </Radio>
                <Radio value="remarket" isDisabled={isLocked}>
                  Remarket
                </Radio>
              </HStack>
            </RadioGroup>
            {validationErrors?.business_type && (
              <FormErrorMessage>
                {validationErrors.business_type}
              </FormErrorMessage>
            )}
          </FormControl>

          <FormControl
            flex={1}
            isRequired
            isInvalid={!!validationErrors?.is_loa}
          >
            <FormLabel ml="0">Is this an LOA?</FormLabel>
            <RadioGroup
              value={isLoa}
              onChange={handleIsLoaChange}
              isDisabled={isLocked}
            >
              <HStack spacing={8}>
                <Radio value="yes" isDisabled={isLocked}>
                  Yes
                </Radio>
                <Radio value="no" isDisabled={isLocked}>
                  No
                </Radio>
              </HStack>
            </RadioGroup>
            {validationErrors?.is_loa && (
              <FormErrorMessage>{validationErrors.is_loa}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl
            flex={1}
            isRequired
            isInvalid={!!validationErrors?.premium}
          >
            <FormLabel ml="0">Premium</FormLabel>
            <NumberInput
              value={premiumInput}
              onChange={handlePremiumChange}
              min={0}
              step={0.01}
              precision={2}
              isDisabled={isLocked && !canEditPremium}
            >
              <NumberInputField
                placeholder="Enter premium amount"
                borderColor={!premium ? 'red' : 'inherit'}
              />
            </NumberInput>
            {validationErrors?.premium && (
              <FormErrorMessage>{validationErrors.premium}</FormErrorMessage>
            )}
            {premium > 10000 && (
              <Alert
                status="warning"
                mt={2}
                flexDirection="column"
                alignItems="flex-start"
              >
                <HStack mb={2}>
                  <AlertIcon />
                  <AlertDescription>
                    Premium amount exceeds $10,000. Please verify this amount is
                    correct.
                  </AlertDescription>
                </HStack>
                <Checkbox
                  ml={8}
                  isChecked={premiumConfirmed}
                  onChange={(e) =>
                    dispatch(setPremiumConfirmed(e.target.checked))
                  }
                  colorScheme="orange"
                  isRequired
                  isDisabled={isLocked}
                >
                  <Text fontWeight="bold">
                    I confirm the premium amount is correct
                  </Text>
                </Checkbox>
              </Alert>
            )}
          </FormControl>
        </HStack>

        <HStack spacing={4} align="stretch">
          <FormControl
            flex={1}
            isRequired
            isInvalid={!!validationErrors?.client_name}
          >
            <FormLabel ml="0">Client Name</FormLabel>
            <Input
              variant="main"
              value={clientName}
              onChange={handleClientNameChange}
              placeholder="Enter client name"
              isDisabled={isLocked}
              borderColor={!clientName ? 'red' : 'inherit'}
            />
            {validationErrors?.client_name && (
              <FormErrorMessage>
                {validationErrors.client_name}
              </FormErrorMessage>
            )}
          </FormControl>

          <FormControl
            flex={1}
            isRequired
            isInvalid={!!validationErrors?.customer_code}
          >
            <FormLabel ml="0">Customer Code</FormLabel>
            <Input
              variant="main"
              value={customerCode}
              onChange={handleCustomerCodeChange}
              placeholder="Enter customer code"
              isDisabled={isLocked}
              borderColor={!customerCode ? 'red' : 'inherit'}
            />
            {validationErrors?.customer_code && (
              <FormErrorMessage>
                {validationErrors.customer_code}
              </FormErrorMessage>
            )}
          </FormControl>
        </HStack>

        {isCommercialAutoSigningEnabled && (
          <HStack spacing={4} align="stretch">
            <FormControl
              flex={1}
              isRequired
              isInvalid={!!validationErrors?.applicant_address?.address}
            >
              <FormLabel ml="0">Address</FormLabel>
              <Input
                variant="main"
                value={applicantAddress.address}
                onChange={(e) =>
                  dispatch(
                    setApplicantAddressField({
                      field: 'address',
                      value: e.target.value,
                    }),
                  )
                }
                placeholder="Enter address"
                isDisabled={isLocked}
                borderColor={!applicantAddress.address ? 'red' : 'inherit'}
              />
              {validationErrors?.applicant_address?.address && (
                <FormErrorMessage>
                  {validationErrors.applicant_address.address}
                </FormErrorMessage>
              )}
            </FormControl>

            <FormControl
              flex={1}
              isRequired
              isInvalid={!!validationErrors?.applicant_address?.city}
            >
              <FormLabel ml="0">City</FormLabel>
              <Input
                variant="main"
                value={applicantAddress.city}
                onChange={(e) =>
                  dispatch(
                    setApplicantAddressField({
                      field: 'city',
                      value: e.target.value,
                    }),
                  )
                }
                placeholder="Enter city"
                isDisabled={isLocked}
                borderColor={!applicantAddress.city ? 'red' : 'inherit'}
              />
              {validationErrors?.applicant_address?.city && (
                <FormErrorMessage>
                  {validationErrors.applicant_address.city}
                </FormErrorMessage>
              )}
            </FormControl>

            <FormControl
              flex={1}
              isRequired
              isInvalid={!!validationErrors?.applicant_address?.postal_code}
            >
              <FormLabel ml="0">Postal Code</FormLabel>
              <Input
                variant="main"
                value={applicantAddress.postal_code}
                onChange={(e) =>
                  dispatch(
                    setApplicantAddressField({
                      field: 'postal_code',
                      value: e.target.value,
                    }),
                  )
                }
                placeholder="Enter postal code"
                isDisabled={isLocked}
                borderColor={!applicantAddress.postal_code ? 'red' : 'inherit'}
              />
              {validationErrors?.applicant_address?.postal_code && (
                <FormErrorMessage>
                  {validationErrors.applicant_address.postal_code}
                </FormErrorMessage>
              )}
            </FormControl>
          </HStack>
        )}
      </VStack>
    </Card>
  );
};

export default CommercialPolicyInformation;
